import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import {
  Search,
  MapPin,
  Star,
  ArrowRight,
  Briefcase,
  Wind,
  Sun,
  Tool,
  Droplet,
  Home as HomeIcon,
  Scissors,
  Zap,
  Truck,
  Trash2,
  Settings,
  Thermometer,
  CloudSnow,
  CloudRain,
  Feather,
  Heart,
  Shield,
  Award,
  Box,
  Package,
  PenTool,
  Edit3,
  Layers,
  Grid,
  Circle,
  HelpCircle
} from 'react-feather';
import { API_BASE_URL } from '../config/api';
import { authFetch } from '../utils/authFetch';
import ServiceSearchAutocomplete from '../components/common/ServiceSearchAutocomplete';
import { useIsMobile } from '../hooks/useBreakpoint';
import { useAuth } from '../context/AuthContext';

/* Icon map — mirrors ServiceSearchAutocomplete so category grid shows correct icons */
const iconMap: { [key: string]: React.ComponentType<{ size?: number; color?: string }> } = {
  briefcase: Briefcase, wind: Wind, sun: Sun, tool: Tool, droplet: Droplet,
  home: HomeIcon, scissors: Scissors, zap: Zap, truck: Truck,
  trash: Trash2, trash2: Trash2, settings: Settings, thermometer: Thermometer,
  snow: CloudSnow, cloudsnow: CloudSnow, rain: CloudRain, cloudrain: CloudRain,
  leaf: Feather, feather: Feather, flower: Star, star: Star, heart: Heart,
  shield: Shield, award: Award, box: Box, package: Package,
  wrench: Tool, hammer: Tool, pentool: PenTool, edit: Edit3,
  layers: Layers, grid: Grid, circle: Circle, search: Search, helpcircle: HelpCircle
};

const getIconComponent = (iconName?: string): React.ComponentType<{ size?: number; color?: string }> | null => {
  if (!iconName) return null;
  return iconMap[iconName.toLowerCase().replace(/[^a-z0-9]/g, '')] || null;
};

interface Service {
  id: number;
  name: string;
  icon?: string;
  description?: string;
  isOther?: boolean;
}

interface Contractor {
  id: number;
  name: string;
  businessName?: string;
  rating?: number;
  reviewCount?: number;
  services?: { name: string }[];
  profilePicture?: string;
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, userType, isLoading } = useAuth();
  const [serviceSearchValue, setServiceSearchValue] = useState('');
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedCity, setSelectedCity] = useState('Nampa');
  const [cities, setCities] = useState<string[]>(['Nampa', 'Caldwell', 'Boise', 'Meridian', 'Eagle']);
  const [services, setServices] = useState<Service[]>([]);
  const [featuredContractors, setFeaturedContractors] = useState<Contractor[]>([]);
  const isMobile = useIsMobile();

  // Fetch cities from database
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await authFetch(`${API_BASE_URL}/service-areas`);
        if (response.ok) {
          const serviceAreas = await response.json();
          const cityNames = serviceAreas.map((area: any) => area.name);
          if (cityNames.length > 0) {
            setCities(cityNames);
            if (!cityNames.includes(selectedCity)) {
              setSelectedCity(cityNames[0]);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch cities:', error);
      }
    };
    fetchCities();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch services for category grid
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await authFetch(`${API_BASE_URL}/services`);
        if (response.ok) {
          const data = await response.json();
          const list = data.services || data;
          if (Array.isArray(list)) setServices(list);
        }
      } catch (error) {
        console.error('Failed to fetch services:', error);
      }
    };
    fetchServices();
  }, []);

  // Fetch featured contractors
  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await authFetch(`${API_BASE_URL}/contractors`);
        if (response.ok) {
          const data = await response.json();
          const list = data.success ? data.contractors : data;
          if (Array.isArray(list)) {
            const sorted = [...list].sort((a: any, b: any) => (b.rating || 0) - (a.rating || 0));
            setFeaturedContractors(sorted.slice(0, 6));
          }
        }
      } catch (error) {
        console.error('Failed to fetch contractors:', error);
      }
    };
    fetchFeatured();
  }, []);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (selectedService) {
      params.set('service', selectedService.name);
      params.set('serviceId', selectedService.id.toString());
    } else if (serviceSearchValue.trim()) {
      params.set('q', serviceSearchValue.trim());
    }
    if (selectedCity) params.set('city', selectedCity);
    navigate(`/search?${params.toString()}`);
  };

  const handleServiceSelect = (service: Service | null) => {
    setSelectedService(service);
    if (service) setServiceSearchValue(service.name);
  };

  const displayServices = services.filter(s => !s.isOther).slice(0, 8);

  // Show nothing while auth state is loading (Header/Footer still render via MainLayout)
  if (isLoading) return null;

  // Redirect contractors straight to their dashboard
  if (userType === 'contractor') {
    return <Navigate to="/contractor-dashboard" replace />;
  }

  return (
    <div style={{ backgroundColor: 'var(--color-bg-secondary)' }}>

      {/* ============ SEARCH BAR ============ */}
      <div style={{
        backgroundColor: 'var(--color-bg-primary)',
        borderBottom: '1px solid var(--color-border-default)',
        padding: isMobile ? 'var(--space-8) var(--space-5)' : 'var(--space-10) var(--space-6)'
      }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div style={{
            display: 'flex',
            alignItems: 'stretch',
            border: '1px solid var(--color-border-default)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
            backgroundColor: 'var(--color-bg-primary)',
            boxShadow: 'var(--shadow-lg)',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <div style={{ flex: 2, minWidth: 0 }}>
              <ServiceSearchAutocomplete
                value={serviceSearchValue}
                onChange={setServiceSearchValue}
                onSelect={handleServiceSelect}
                placeholder="What do you need help with?"
                style={{
                  border: 'none',
                  padding: '14px 40px',
                  fontSize: 'var(--text-base)',
                  outline: 'none',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{
              position: 'relative',
              borderLeft: isMobile ? 'none' : '1px solid var(--color-border-default)',
              borderTop: isMobile ? '1px solid var(--color-border-default)' : 'none',
              flex: isMobile ? undefined : '0 0 160px'
            }}>
              <MapPin size={16} style={{
                position: 'absolute',
                left: 'var(--space-3)',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--color-text-subtle)',
                pointerEvents: 'none'
              }} />
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                aria-label="City"
                style={{
                  width: '100%',
                  padding: '14px var(--space-3) 14px var(--space-8)',
                  border: 'none',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-text-secondary)',
                  outline: 'none',
                  cursor: 'pointer',
                  appearance: 'none',
                  backgroundColor: 'transparent'
                }}
              >
                {cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>

            <button
              onClick={handleSearch}
              style={{
                padding: '14px var(--space-6)',
                backgroundColor: 'var(--btn-cta-bg)',
                color: 'var(--btn-cta-text)',
                border: 'none',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-weight-semibold)' as any,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)',
                whiteSpace: 'nowrap',
                transition: 'var(--transition-fast)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--btn-cta-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--btn-cta-bg)';
              }}
            >
              <Search size={18} />
              Search
            </button>
          </div>
        </div>
      </div>

      {/* ============ SIGNUP NUDGE (unauthenticated only) ============ */}
      {!isAuthenticated && (
        <div style={{
          backgroundColor: 'var(--color-accent-bg-subtle)',
          padding: 'var(--space-3) var(--space-5)',
          textAlign: 'center',
          borderBottom: '1px solid var(--color-border-default)'
        }}>
          <span style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--color-text-secondary)'
          }}>
            Sign up free to book directly with verified pros.{' '}
            <Link
              to="/signup"
              style={{
                color: 'var(--color-accent-icon)',
                fontWeight: 'var(--font-weight-semibold)' as any,
                textDecoration: 'none'
              }}
            >
              Create account
            </Link>
          </span>
        </div>
      )}

      {/* ============ SERVICE CATEGORIES (from API) ============ */}
      {displayServices.length > 0 && (
        <div style={{
          maxWidth: '960px',
          margin: '0 auto',
          padding: 'var(--space-10) var(--space-5)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 'var(--space-6)'
          }}>
            <h2 style={{
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--font-weight-semibold)' as any,
              color: 'var(--color-text-primary)',
              margin: 0
            }}>
              Popular Services
            </h2>
            <button
              onClick={() => navigate('/services')}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)' as any,
                color: 'var(--color-text-muted)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                transition: 'var(--transition-fast)'
              }}
            >
              View all <ArrowRight size={14} />
            </button>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: 'var(--space-4)'
          }}>
            {displayServices.map(service => {
              const IconComp = getIconComponent(service.icon);
              return (
                <div
                  key={service.id}
                  onClick={() => {
                    const params = new URLSearchParams();
                    params.set('service', service.name);
                    params.set('serviceId', service.id.toString());
                    if (selectedCity) params.set('city', selectedCity);
                    navigate(`/search?${params.toString()}`);
                  }}
                  style={{
                    backgroundColor: 'var(--color-bg-primary)',
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--space-5)',
                    border: '1px solid var(--color-border-default)',
                    cursor: 'pointer',
                    transition: 'var(--transition-fast)',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 'var(--space-3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = '';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: 'var(--radius-lg)',
                    backgroundColor: 'var(--color-accent-bg-subtle)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {IconComp ? (
                      <IconComp size={24} color="var(--color-accent-icon)" />
                    ) : (
                      <Tool size={24} color="var(--color-accent-icon)" />
                    )}
                  </div>
                  <div style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-semibold)' as any,
                    color: 'var(--color-text-primary)'
                  }}>
                    {service.name}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ============ FEATURED CONTRACTORS (from API) ============ */}
      {featuredContractors.length > 0 && (
        <div style={{
          maxWidth: '960px',
          margin: '0 auto',
          padding: '0 var(--space-5) var(--space-10)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 'var(--space-6)'
          }}>
            <h2 style={{
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--font-weight-semibold)' as any,
              color: 'var(--color-text-primary)',
              margin: 0
            }}>
              Featured Pros
            </h2>
            <button
              onClick={() => navigate('/search')}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)' as any,
                color: 'var(--color-text-muted)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                transition: 'var(--transition-fast)'
              }}
            >
              View all <ArrowRight size={14} />
            </button>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: 'var(--space-4)'
          }}>
            {featuredContractors.map(contractor => {
              const displayName = contractor.businessName || contractor.name;
              const initial = displayName ? displayName[0].toUpperCase() : '?';
              const serviceNames = contractor.services?.map(s => s.name).slice(0, 3) || [];
              return (
                <div
                  key={contractor.id}
                  onClick={() => navigate(`/contractor/${contractor.id}`)}
                  style={{
                    backgroundColor: 'var(--color-bg-primary)',
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--space-5)',
                    border: '1px solid var(--color-border-default)',
                    cursor: 'pointer',
                    transition: 'var(--transition-fast)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = '';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    marginBottom: 'var(--space-3)'
                  }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: 'var(--radius-full)',
                      backgroundColor: 'var(--color-bg-tertiary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontSize: 'var(--text-base)',
                      fontWeight: 'var(--font-weight-semibold)' as any,
                      color: 'var(--color-text-muted)'
                    }}>
                      {initial}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 'var(--text-base)',
                        fontWeight: 'var(--font-weight-semibold)' as any,
                        color: 'var(--color-text-primary)'
                      }}>
                        {displayName}
                      </div>
                      <div style={{
                        fontSize: 'var(--text-sm)',
                        color: 'var(--color-text-muted)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)'
                      }}>
                        {contractor.rating ? (
                          <>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', color: 'var(--color-accent-icon)' }}>
                              <Star size={12} fill="currentColor" />
                              {contractor.rating.toFixed(1)}
                            </span>
                            {contractor.reviewCount ? (
                              <>
                                <span>·</span>
                                <span>{contractor.reviewCount} reviews</span>
                              </>
                            ) : null}
                          </>
                        ) : (
                          <span>New pro</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {serviceNames.length > 0 && (
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 'var(--space-1)'
                    }}>
                      {serviceNames.map((name, idx) => (
                        <span
                          key={idx}
                          style={{
                            fontSize: 'var(--text-xs)',
                            fontWeight: 'var(--font-weight-medium)' as any,
                            color: 'var(--color-text-secondary)',
                            backgroundColor: 'var(--color-bg-secondary)',
                            padding: 'var(--space-1) var(--space-2)',
                            borderRadius: 'var(--radius-full)'
                          }}
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
